import { create } from 'zustand'

// =============================================================================
// TEMP ID GENERATOR — Monotonic counter to avoid Date.now() collisions
// =============================================================================

let _tempIdCounter = 0
/** Generate a unique negative temp ID (negative to distinguish from server IDs) */
export const nextTempId = (): number => -(++_tempIdCounter)

// Character status auto-dismiss timer
let _characterStatusTimer: ReturnType<typeof setTimeout> | null = null

// =============================================================================
// SYSTEM PANEL (DEC-047) — Debug data from hidden agents
// =============================================================================

// =============================================================================
// DEBUG EVENT (DEC-047) — Inline agent bubbles in chat
// =============================================================================

export type DebugAgentType =
  | 'ContentAnalyzer'
  | 'ContextObserver'
  | 'CrisisDetector'
  | 'VoiceAnalysis'
  | 'ImageAnalysis'
  | 'PharmaAdvisor'
  | 'Consilium'
  | 'DossierUpdater'
  | 'RAGRetriever'
  | 'KnowledgeBase'
  | 'SessionSummarizer'
  | 'Orchestrator'
  | 'Performance'
  | 'ResponseFlow'
  | 'TherapistContext'

export interface DebugEvent {
  id: string  // Unique ID for React key
  agent: DebugAgentType
  icon: string
  data: Record<string, unknown>
  timestamp: string
  messageId: number  // Associated user message ID
}

// Agent icons per DEC-047 spec
export const DEBUG_AGENT_ICONS: Record<DebugAgentType, string> = {
  ContentAnalyzer: '🔍',
  ContextObserver: '👁️',
  CrisisDetector: '🚨',
  VoiceAnalysis: '🎤',
  ImageAnalysis: '📷',
  PharmaAdvisor: '💊',
  Consilium: '🧠',
  DossierUpdater: '📋',
  RAGRetriever: '🔎',
  KnowledgeBase: '📚',
  SessionSummarizer: '📝',
  Orchestrator: '🎯',
  Performance: '⚡',
  ResponseFlow: '📡',
  TherapistContext: '📝',
}

// =============================================================================
// ADVANCED DEBUG (DEC-050) — WS and Server log entries
// =============================================================================

export interface WsLogEntry {
  id: string
  timestamp: string
  direction: 'in' | 'out'
  type: string
  summary: string      // first 120 chars of payload
  rawData: unknown     // full payload for expand
}

export interface ServerLogEntry {
  id: string
  timestamp: string
  level: 'DEBUG' | 'INFO' | 'WARNING' | 'ERROR'
  logger: string
  message: string
}

// =============================================================================
// SYSTEM PANEL (DEC-047) — Debug data from hidden agents
// =============================================================================

export interface SystemPanelData {
  content_analysis?: {
    crisis?: {
      level: string  // 'red' | 'orange' | 'yellow' | 'none'
      signals: string[]
      immediate_danger: boolean
      needs_finch: boolean
    }
    emotion?: {
      primary: string
      secondary?: string
      intensity: number
      valence: number
      confidence: number
    }
    medication?: {
      detected: boolean
      medications: string[]
      tapering_intent: boolean
      needs_pharma_consult: boolean
    }
    learning?: {
      detected: boolean
      type: string
      content: string
      confidence: number
    }
    activity?: {
      type: string
      activity: string
      expected_minutes?: number
    }
    handoff?: {
      requested: boolean
      target: string
      urgency: string
    }
    promise?: {
      detected: boolean
      time_expression: string
    }
    media?: {
      should_send_video: boolean
      should_ask_photo: boolean
      user_requests_video: boolean
      reason: string
    }
  }
  response_flow?: {
    message_number: number
    send_voice: boolean
    send_video: boolean
    emotion_intensity?: number
    is_emotional?: boolean
  }
  multimodal_analysis?: {
    debug_output: string
  }
  voice_analysis?: {
    prosody_context: string
  }
  guardian?: {
    violations: string[]
    warnings: string[]
    status: string
  }
}

// =============================================================================
// DEBUG INFO (DEC-050) — Enhanced debug data for admin mode
// =============================================================================

export type CrisisLevel = 'none' | 'low' | 'medium' | 'high' | 'critical'

export interface ContentAnalysisDebug {
  intent?: string
  emotion?: string
  emotion_confidence?: number
  crisis_level?: CrisisLevel
  routing_decision?: string
}

export interface WordEmotionDebug {
  word: string
  emotion: string
  confidence: number
  source: 'voice' | 'face' | 'both'
}

export interface VoiceAnalysisDebug {
  words: WordEmotionDebug[]
  dominant_emotion?: string
  mismatch_count?: number
}

export interface FaceAnalysisDebug {
  frames_analyzed: number
  emotions: Record<string, number>
  mismatches: string[]
}

export interface GenerationDebug {
  model?: string
  tokens_in?: number
  tokens_out?: number
  latency_ms?: number
  escalated?: boolean
  escalation_reason?: string
  cache_hit?: boolean
}

export interface TTSDebug {
  provider?: string
  voice_id?: string
  duration_ms?: number
  generation_ms?: number
}

export interface VideoDebug {
  provider?: string
  avatar_id?: string
  duration_ms?: number
  generation_ms?: number
  lipsync_quality?: number
}

export interface MessageDebugInfo {
  content_analysis?: ContentAnalysisDebug
  voice_analysis?: VoiceAnalysisDebug
  face_analysis?: FaceAnalysisDebug
  generation?: GenerationDebug
  tts?: TTSDebug
  video?: VideoDebug
}

// =============================================================================
// MESSAGE & CHAT STATE
// =============================================================================

// Message delivery status (Telegram-style)
export type MessageStatus = 'sending' | 'sent' | 'seen' | 'error'

// Reaction on a message (Telegram 7 emoji set)
export interface Reaction {
  emoji: string
  count: number
  reactedByMe: boolean
}

export interface Message {
  id: number
  role: 'user' | 'assistant' | 'system'
  content: string
  mediaType: string
  status?: MessageStatus  // Delivery status for user messages
  emotionDetected?: string
  emotionConfidence?: number
  transcription?: string
  createdAt: string
  voiceUrl?: string
  videoUrl?: string  // Single video (legacy, also first of multi-circle)
  videoUrls?: string[]  // Multi-circle videos (all URLs)
  totalVideos?: number  // Total expected videos for progress indication
  imageUrl?: string
  duration?: number  // Media duration in seconds
  systemPanel?: SystemPanelData  // Debug data from hidden agents (DEC-047)
  debugInfo?: MessageDebugInfo  // Enhanced debug data (DEC-050)
  // Reply fields
  replyToMessageId?: number
  quotedText?: string
  // Edit tracking
  editedAt?: string
  // Reactions
  reactions?: Reaction[]
  // Soft delete
  isDeleted?: boolean
  // Cross-session scroll
  sessionId?: number
}

export interface Therapist {
  type: string
  name: string
  avatarUrl?: string
  voiceId?: string
}

// Telegram-style therapist status
export type TherapistStatus =
  | 'idle'
  | 'typing'
  | 'recording_audio'
  | 'recording_video'
  | 'listening_audio'
  | 'watching_video'
  | 'analyzing_photo'
  | 'generating_voice'
  | 'character'  // Character-driven status (e.g., "смотрит с теплотой...")

// Available playback speeds
export const PLAYBACK_RATES = [1, 1.25, 1.5] as const
export type PlaybackRate = (typeof PLAYBACK_RATES)[number]

// Proactive prompt types
export type ProactiveType =
  | 'mood_check'
  | 'selfie_request'
  | 'video_checkin'
  | 'gentle_ping'
  | 'daily_reflection'
  | 'goal_checkin'
  | 'consilium_proposal'

// =============================================================================
// INTERACTIVE CONSILIUM (Phase 1B)
// =============================================================================

export type ConsiliumPhase =
  | 'gathering'
  | 'opening'
  | 'discussion'
  | 'user_joining'
  | 'pre_synthesis'
  | 'synthesis'
  | 'completed'

export interface ConsiliumPromptButton {
  action: string
  label: string
}

export interface ConsiliumPrompt {
  promptType: 'join_discussion' | 'pre_synthesis'
  message?: string  // Optional message from Harold
  buttons: ConsiliumPromptButton[]
}

export interface ConsiliumTurn {
  id: string
  speaker: string        // "finch", "wendy", "artem", "una", "user"
  content: string
  phase: ConsiliumPhase
  turnNumber: number
  timestamp: string
}

export interface ConsiliumActionStep {
  step: string
  why: string
  timeline: string
  priority: 'high' | 'medium' | 'low'
}

export interface ConsiliumActionPlan {
  synthesis: string
  keyInsights: string[]
  actionSteps: ConsiliumActionStep[]
  goals: Array<{ description: string; timeline: string; therapist?: string }>
  focusAreas: string[]
  nextSessionGuidance: Record<string, string>
  nextConsilium: { recommendedIn: string; triggers: string[] } | null
}

export interface ProactivePrompt {
  id: number
  type: ProactiveType
  message: string
  therapist: string
  buttons?: Array<{
    text: string
    action: string
    value?: string | number
  }>
  timestamp: string
}

interface ChatState {
  messages: Message[]
  sessionId: number | null
  currentTherapist: Therapist | null
  therapistStatus: TherapistStatus
  characterStatusText: string | null  // Custom text for 'character' status
  isConnected: boolean
  connectionFailed: boolean
  playbackRate: PlaybackRate
  proactivePrompt: ProactivePrompt | null

  // Audio/video playback queue (prevents simultaneous playback)
  playbackQueue: number[]  // Message IDs waiting to play
  currentlyPlayingId: number | null  // Message ID currently playing

  // Debug events (DEC-047) - inline agent bubbles
  debugEvents: DebugEvent[]

  // Advanced debug logs (DEC-050)
  wsLog: WsLogEntry[]
  serverLogs: ServerLogEntry[]

  // Debug error tracking (DEC-053 A6)
  hasDebugErrors: boolean

  // Interactive Consilium (Phase 1B)
  consiliumActive: boolean
  consiliumPhase: ConsiliumPhase | null
  consiliumTurns: ConsiliumTurn[]
  consiliumActionPlan: ConsiliumActionPlan | null
  consiliumPrompt: ConsiliumPrompt | null

  // Reply/Edit state
  replyingTo: Message | null
  editingMessage: Message | null

  // Infinite scroll state
  isLoadingHistory: boolean
  hasMoreHistory: boolean
  sessionMeta: Record<number, { therapist: string; startedAt: string }>

  // Actions
  addMessage: (message: Message) => void
  setMessages: (messages: Message[]) => void
  updateMessage: (id: number, updates: Partial<Message>) => void  // Update single message
  updateLastMessageWhere: (predicate: (m: Message) => boolean, updates: Partial<Message>) => void  // Atomic find-last + update
  setSessionId: (id: number) => void
  setTherapist: (therapist: Therapist) => void
  setTherapistStatus: (status: TherapistStatus) => void
  setCharacterStatus: (text: string | null, durationSec?: number) => void  // Set character status text with optional auto-dismiss
  setConnected: (connected: boolean) => void
  setConnectionFailed: (failed: boolean) => void
  clearMessages: () => void
  cyclePlaybackRate: () => void
  setPlaybackRate: (rate: PlaybackRate) => void
  setProactivePrompt: (prompt: ProactivePrompt | null) => void

  // Playback queue actions
  enqueuePlayback: (messageId: number) => void  // Add to queue
  startPlayback: (messageId: number) => void    // Mark as playing
  finishPlayback: () => void                    // Done, play next
  canPlay: (messageId: number) => boolean       // Check if can play now

  // Debug event actions (DEC-047)
  addDebugEvent: (event: DebugEvent) => void
  getDebugEventsForMessage: (messageId: number) => DebugEvent[]
  clearDebugEvents: () => void

  // Advanced debug log actions (DEC-050)
  addWsLogEntry: (entry: WsLogEntry) => void
  addServerLog: (entry: ServerLogEntry) => void
  prependServerLogs: (entries: ServerLogEntry[]) => void
  clearWsLog: () => void
  clearServerLogs: () => void
  clearDebugErrors: () => void

  // Reply/Edit actions
  setReplyingTo: (message: Message | null) => void
  setEditingMessage: (message: Message | null) => void

  // Reaction actions
  updateMessageReactions: (id: number, reactions: Reaction[]) => void

  // Delete actions
  markMessageDeleted: (id: number) => void
  markMessageEdited: (id: number, newContent: string, editedAt: string) => void

  // Infinite scroll actions
  prependMessages: (messages: Message[]) => void
  setLoadingHistory: (loading: boolean) => void
  setHasMoreHistory: (hasMore: boolean) => void
  addSessionMeta: (id: number, meta: { therapist: string; startedAt: string }) => void

  // Interactive Consilium actions (Phase 1B)
  startConsilium: () => void
  addConsiliumTurn: (turn: ConsiliumTurn) => void
  setConsiliumPhase: (phase: ConsiliumPhase) => void
  setConsiliumActionPlan: (plan: ConsiliumActionPlan) => void
  setConsiliumPrompt: (prompt: ConsiliumPrompt | null) => void
  endConsilium: () => void
}

export const useChatStore = create<ChatState>((set, get) => ({
  messages: [],
  sessionId: null,
  currentTherapist: null,
  therapistStatus: 'idle',
  characterStatusText: null,
  isConnected: false,
  connectionFailed: false,
  playbackRate: 1,
  proactivePrompt: null,
  playbackQueue: [],
  currentlyPlayingId: null,
  debugEvents: [],
  wsLog: [],
  serverLogs: [],
  hasDebugErrors: false,
  consiliumActive: false,
  consiliumPhase: null,
  consiliumTurns: [],
  consiliumActionPlan: null,
  consiliumPrompt: null,
  replyingTo: null,
  editingMessage: null,
  isLoadingHistory: false,
  hasMoreHistory: true,
  sessionMeta: {},

  addMessage: (message) =>
    set((state) => {
      // Deduplicate by message_id to prevent duplicate messages from multiple WS connections
      const exists = state.messages.some((m) => m.id === message.id)
      if (exists) {
        console.warn('[DIAG] addMessage DEDUP: dropping message id=', message.id, 'role=', message.role, '— already exists in store')
        return state // Don't add duplicate
      }
      console.log('[DIAG] addMessage OK: id=', message.id, 'role=', message.role, 'total=', state.messages.length + 1)
      return { messages: [...state.messages, message] }
    }),

  setMessages: (messages) => set({ messages }),

  updateMessage: (id, updates) =>
    set((state) => {
      const index = state.messages.findIndex((m) => m.id === id)
      if (index === -1) return state
      const updatedMessages = [...state.messages]
      updatedMessages[index] = { ...updatedMessages[index], ...updates }
      return { messages: updatedMessages }
    }),

  updateLastMessageWhere: (predicate, updates) =>
    set((state) => {
      for (let i = state.messages.length - 1; i >= 0; i--) {
        if (predicate(state.messages[i])) {
          const updatedMessages = [...state.messages]
          updatedMessages[i] = { ...updatedMessages[i], ...updates }
          return { messages: updatedMessages }
        }
      }
      return state
    }),

  setSessionId: (sessionId) => set({ sessionId }),

  setTherapist: (currentTherapist) => set({ currentTherapist }),

  setTherapistStatus: (therapistStatus) => set({ therapistStatus }),

  setCharacterStatus: (characterStatusText, durationSec) => {
    // Clear any existing timer
    if (_characterStatusTimer) {
      clearTimeout(_characterStatusTimer)
      _characterStatusTimer = null
    }
    set({ characterStatusText })
    // Auto-dismiss after duration (default 7s)
    if (characterStatusText && durationSec !== 0) {
      const ms = (durationSec || 7) * 1000
      _characterStatusTimer = setTimeout(() => {
        _characterStatusTimer = null
        set({ therapistStatus: 'idle' as TherapistStatus, characterStatusText: null })
      }, ms)
    }
  },

  setConnected: (isConnected) => set({ isConnected, connectionFailed: false }),
  setConnectionFailed: (connectionFailed) => set({ connectionFailed }),

  clearMessages: () => set({ messages: [], sessionId: null, playbackQueue: [], currentlyPlayingId: null, debugEvents: [], wsLog: [], serverLogs: [], hasDebugErrors: false, isLoadingHistory: false, hasMoreHistory: true, sessionMeta: {} }),

  cyclePlaybackRate: () =>
    set((state) => {
      const currentIndex = PLAYBACK_RATES.indexOf(state.playbackRate)
      const nextIndex = (currentIndex + 1) % PLAYBACK_RATES.length
      const newRate = PLAYBACK_RATES[nextIndex]
      // Save to backend (fire and forget)
      import('../api/preferences').then(({ updatePreferences }) => {
        updatePreferences({ playback_rate: newRate }).catch(() => {})
      })
      return { playbackRate: newRate }
    }),

  setPlaybackRate: (rate) => set({ playbackRate: rate }),

  setProactivePrompt: (proactivePrompt) => set({ proactivePrompt }),

  // Playback queue: ensures only one audio/video plays at a time
  enqueuePlayback: (messageId) =>
    set((state) => {
      // Don't add duplicates
      if (state.playbackQueue.includes(messageId)) return state
      return { playbackQueue: [...state.playbackQueue, messageId] }
    }),

  startPlayback: (messageId) =>
    set((state) => ({
      currentlyPlayingId: messageId,
      playbackQueue: state.playbackQueue.filter((id) => id !== messageId),
    })),

  finishPlayback: () =>
    set({ currentlyPlayingId: null }),  // Clear current, next in queue will check canPlay

  canPlay: (messageId) => {
    const state = get()
    // Can play if nothing is playing and either queue is empty or this is first in queue
    if (state.currentlyPlayingId !== null) return false
    if (state.playbackQueue.length === 0) return true
    return state.playbackQueue[0] === messageId
  },

  // Debug event actions (DEC-047)
  addDebugEvent: (event) =>
    set((state) => {
      // Resolve messageId: if server ID doesn't match any message (temp ID case),
      // bind to the last user message so debug events appear in real-time
      let resolvedId = event.messageId
      const hasMatch = state.messages.some((m) => m.id === resolvedId)
      if (!hasMatch) {
        const lastUserMsg = [...state.messages].reverse().find((m) => m.role === 'user')
        if (lastUserMsg) resolvedId = lastUserMsg.id
      }
      const resolved = { ...event, messageId: resolvedId }

      // Deduplicate by messageId + agent (same agent shouldn't fire twice for same message)
      const exists = state.debugEvents.some(
        (e) => e.messageId === resolved.messageId && e.agent === resolved.agent
      )
      if (exists) {
        return state // Don't add duplicate
      }
      const updated = [...state.debugEvents, resolved]
      // Cap at 500 entries, prune oldest
      return { debugEvents: updated.length > 500 ? updated.slice(-500) : updated }
    }),

  getDebugEventsForMessage: (messageId) => {
    const state = get()
    return state.debugEvents.filter((e) => e.messageId === messageId)
  },

  clearDebugEvents: () => set({ debugEvents: [] }),

  // Advanced debug log actions (DEC-050)
  addWsLogEntry: (entry) =>
    set((state) => {
      const updated = [...state.wsLog, entry]
      return { wsLog: updated.length > 1000 ? updated.slice(-1000) : updated }
    }),

  addServerLog: (entry) =>
    set((state) => {
      const updated = [...state.serverLogs, entry]
      return {
        serverLogs: updated.length > 500 ? updated.slice(-500) : updated,
        hasDebugErrors: state.hasDebugErrors || entry.level === 'ERROR',
      }
    }),

  prependServerLogs: (entries) =>
    set((state) => {
      const merged = [...entries, ...state.serverLogs]
      return {
        serverLogs: merged.length > 500 ? merged.slice(-500) : merged,
        hasDebugErrors: state.hasDebugErrors || entries.some(e => e.level === 'ERROR'),
      }
    }),

  clearWsLog: () => set({ wsLog: [] }),
  clearServerLogs: () => set({ serverLogs: [], hasDebugErrors: false }),
  clearDebugErrors: () => set({ hasDebugErrors: false }),

  // Reply/Edit actions
  setReplyingTo: (message) => set({ replyingTo: message }),
  setEditingMessage: (message) => set({ editingMessage: message }),

  // Reaction actions
  updateMessageReactions: (id, reactions) =>
    set((state) => {
      const index = state.messages.findIndex((m) => m.id === id)
      if (index === -1) return state
      const updatedMessages = [...state.messages]
      updatedMessages[index] = { ...updatedMessages[index], reactions }
      return { messages: updatedMessages }
    }),

  // Delete actions
  markMessageDeleted: (id) =>
    set((state) => ({
      messages: state.messages.filter((m) => m.id !== id),
    })),

  markMessageEdited: (id, newContent, editedAt) =>
    set((state) => {
      const index = state.messages.findIndex((m) => m.id === id)
      if (index === -1) return state
      const updatedMessages = [...state.messages]
      updatedMessages[index] = {
        ...updatedMessages[index],
        content: newContent,
        editedAt,
      }
      return { messages: updatedMessages }
    }),

  // Interactive Consilium actions (Phase 1B)
  startConsilium: () =>
    set({ consiliumActive: true, consiliumPhase: 'gathering', consiliumTurns: [], consiliumActionPlan: null, consiliumPrompt: null }),

  addConsiliumTurn: (turn) =>
    set((state) => {
      // Dedup by id
      if (state.consiliumTurns.some((t) => t.id === turn.id)) return state
      const updated = [...state.consiliumTurns, turn]
      return {
        consiliumTurns: updated,
        consiliumPhase: turn.phase,
      }
    }),

  setConsiliumPhase: (consiliumPhase) => set({ consiliumPhase, consiliumActive: true }),

  setConsiliumActionPlan: (consiliumActionPlan) => set({ consiliumActionPlan }),

  setConsiliumPrompt: (consiliumPrompt) => set({ consiliumPrompt }),

  endConsilium: () =>
    set({ consiliumPhase: 'completed' as ConsiliumPhase }),

  // Infinite scroll actions
  prependMessages: (newMessages) =>
    set((state) => {
      const existingIds = new Set(state.messages.map(m => m.id))
      const unique = newMessages.filter(m => !existingIds.has(m.id))
      if (unique.length === 0) return state
      return { messages: [...unique, ...state.messages] }
    }),
  setLoadingHistory: (loading) => set({ isLoadingHistory: loading }),
  setHasMoreHistory: (hasMore) => set({ hasMoreHistory: hasMore }),
  addSessionMeta: (id, meta) =>
    set((state) => ({
      sessionMeta: { ...state.sessionMeta, [id]: meta },
    })),
}))
